import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Pagination,
} from '@mui/material';
import {
  Refresh,
  Download,
  FilterList,
  Search,
  Info,
  Warning,
  Error,
  CheckCircle,
  Archive,
  Visibility,
  GetApp,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { styled } from '@mui/material/styles';
import { activityLogsAPI } from '../services/api';

const SeverityChip = styled(Chip)(({ theme, severity }) => ({
  backgroundColor: 
    severity === 'info' ? theme.palette.info.light :
    severity === 'warning' ? theme.palette.warning.light :
    severity === 'error' ? theme.palette.error.light :
    severity === 'critical' ? theme.palette.error.dark :
    theme.palette.grey.light,
  color: 
    severity === 'info' ? theme.palette.info.contrastText :
    severity === 'warning' ? theme.palette.warning.contrastText :
    severity === 'error' ? theme.palette.error.contrastText :
    severity === 'critical' ? theme.palette.error.contrastText :
    theme.palette.grey.contrastText,
}));

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [filters, setFilters] = useState({
    severity: '',
    userId: '',
    startDate: null,
    endDate: null,
    search: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [logDialog, setLogDialog] = useState(false);
  const [exportDialog, setExportDialog] = useState(false);

  const severities = [
    { value: 'info', label: 'Info', color: 'info' },
    { value: 'warning', label: 'Warning', color: 'warning' },
    { value: 'error', label: 'Error', color: 'error' },
    { value: 'critical', label: 'Critical', color: 'error' },
  ];

  const exportFormats = ['csv', 'json', 'pdf'];

  useEffect(() => {
    fetchLogs();
  }, [pagination.page, pagination.limit, filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
        startDate: filters.startDate?.toISOString(),
        endDate: filters.endDate?.toISOString(),
      };
      
      const data = await activityLogsAPI.getAll(params);
      setLogs(data.logs);
      setPagination(prev => ({
        ...prev,
        total: data.total,
      }));
      setError(null);
    } catch (err) {
      setError('Failed to fetch activity logs');
      console.error('Activity logs fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
    setPagination(prev => ({
      ...prev,
      page: 1,
    }));
  };

  const handlePageChange = (event, page) => {
    setPagination(prev => ({
      ...prev,
      page,
    }));
  };

  const handleViewLog = (log) => {
    setSelectedLog(log);
    setLogDialog(true);
  };

  const handleExport = async (format) => {
    try {
      const params = {
        format,
        startDate: filters.startDate?.toISOString(),
        endDate: filters.endDate?.toISOString(),
        severity: filters.severity,
      };
      
      const result = await activityLogsAPI.export(params);
      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = `activity-logs-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setExportDialog(false);
    } catch (err) {
      setError('Failed to export logs');
      console.error('Export error:', err);
    }
  };

  const handleArchive = async () => {
    try {
      const olderThan = new Date();
      olderThan.setDate(olderThan.getDate() - 7); // Archive logs older than 7 days
      
      await activityLogsAPI.archive(olderThan.toISOString());
      setSuccess('Logs archived successfully');
      await fetchLogs();
    } catch (err) {
      setError('Failed to archive logs');
      console.error('Archive error:', err);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'info': return <Info color="info" />;
      case 'warning': return <Warning color="warning" />;
      case 'error': return <Error color="error" />;
      case 'critical': return <Error color="error" />;
      default: return <Info color="default" />;
    }
  };

  const formatLogDetails = (details) => {
    if (typeof details === 'object') {
      return JSON.stringify(details, null, 2);
    }
    return details;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Activity Logs
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<Archive />}
              onClick={handleArchive}
              sx={{ mr: 1 }}
            >
              Archive Old Logs
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => setExportDialog(true)}
              sx={{ mr: 1 }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={fetchLogs}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ mb: 3 }}>
          <Tab label="Live Logs (7 days)" />
          <Tab label="Archive (7 years)" />
        </Tabs>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Severity</InputLabel>
                  <Select
                    value={filters.severity}
                    onChange={(e) => handleFilterChange('severity', e.target.value)}
                    label="Severity"
                  >
                    <MenuItem value="">All</MenuItem>
                    {severities.map((severity) => (
                      <MenuItem key={severity.value} value={severity.value}>
                        {severity.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="User ID"
                  value={filters.userId}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Search"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={fetchLogs}
                  sx={{ height: '56px' }}
                >
                  Apply
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {selectedTab === 0 ? 'Live Activity Logs' : 'Archived Activity Logs'}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Resource</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(log.timestamp).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.userId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.action}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.resource}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {getSeverityIcon(log.severity)}
                          <SeverityChip
                            label={log.severity}
                            size="small"
                            severity={log.severity}
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {log.ipAddress}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewLog(log)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={Math.ceil(pagination.total / pagination.limit)}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          </CardContent>
        </Card>

        {/* Log Details Dialog */}
        <Dialog open={logDialog} onClose={() => setLogDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Log Details</DialogTitle>
          <DialogContent>
            {selectedLog && (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Timestamp
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedLog.timestamp).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      User ID
                    </Typography>
                    <Typography variant="body1">
                      {selectedLog.userId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Action
                    </Typography>
                    <Typography variant="body1">
                      {selectedLog.action}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Resource
                    </Typography>
                    <Typography variant="body1">
                      {selectedLog.resource}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Severity
                    </Typography>
                    <Box display="flex" alignItems="center">
                      {getSeverityIcon(selectedLog.severity)}
                      <SeverityChip
                        label={selectedLog.severity}
                        size="small"
                        severity={selectedLog.severity}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      IP Address
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {selectedLog.ipAddress}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Details
                    </Typography>
                    <Paper sx={{ p: 2, mt: 1, backgroundColor: 'grey.50' }}>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                      >
                        {formatLogDetails(selectedLog.details)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLogDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Export Dialog */}
        <Dialog open={exportDialog} onClose={() => setExportDialog(false)}>
          <DialogTitle>Export Activity Logs</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Choose the format for exporting activity logs:
            </Typography>
            <List>
              {exportFormats.map((format) => (
                <ListItem
                  key={format}
                  button
                  onClick={() => handleExport(format)}
                >
                  <ListItemIcon>
                    <GetApp />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Export as ${format.toUpperCase()}`}
                    secondary={`Download logs in ${format} format`}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExportDialog(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ActivityLogs;
